import { useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, Button, Table, Badge, Alert, Form, Spinner } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader.jsx';
import { fmtMoney, fmtDate } from '../lib/format.js';

// Tiny inline editor: shows current value, becomes a controlled input when
// clicked, persists to /expenses/:id on blur or Enter. Intentionally narrow —
// the full-fledged editor is on /expenses?edit=…
function InlineField({ value, type = 'text', onSave, suffix = '', placeholder = '—', width = 90, align = 'left' }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? '');
  const [busy, setBusy] = useState(false);
  const commit = async () => {
    setEditing(false);
    if (val === (value ?? '')) return;
    setBusy(true);
    try { await onSave(val); }
    finally { setBusy(false); }
  };
  if (!editing) {
    return (
      <span
        onClick={() => { setVal(value ?? ''); setEditing(true); }}
        className="inline-edit-trigger"
        style={{ cursor: 'pointer', borderBottom: '1px dashed var(--bs-border-color)' }}
        title="Klicken zum Bearbeiten">
        {busy ? <Spinner size="sm" /> : (value || <span className="text-muted">{placeholder}</span>)}{suffix}
      </span>
    );
  }
  return (
    <Form.Control
      size="sm" autoFocus type={type} value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
      style={{ width, display: 'inline-block', textAlign: align }} />
  );
}

/**
 * Bulk-Import: mehrere Belege auf einmal droppen, je einen Ausgaben-Entwurf
 * pro Datei anlegen lassen, OCR füllt die Felder. Ergebnisse werden als
 * Review-Liste angezeigt; bei Bedarf kann jeder Entwurf einzeln editiert
 * oder gelöscht werden.
 */
export default function BulkImportPage() {
  const navigate = useNavigate();
  const fileInput = useRef(null);
  const [items, setItems] = useState([]);   // { fileName, status, expense, fieldsFilled, error }
  const [dragOver, setDragOver] = useState(false);
  const [running, setRunning] = useState(false);

  const handleFiles = async (filesLike) => {
    const files = Array.from(filesLike).filter(Boolean);
    if (!files.length) return;

    // Initial in-progress rows so the user sees activity immediately.
    const startIndex = items.length;
    setItems(prev => [
      ...prev,
      ...files.map(f => ({ fileName: f.name, status: 'pending' }))
    ]);
    setRunning(true);

    // Sequenziell hochladen, damit der Server nicht überflutet wird (OCR ist CPU-intensiv).
    for (let i = 0; i < files.length; i++) {
      const idx = startIndex + i;
      const file = files[i];
      try {
        const fd = new FormData();
        fd.append('file', file);
        const r = await api.post('/expenses/import', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setItems(prev => {
          const copy = [...prev];
          if (r.data.duplicate) {
            copy[idx] = {
              fileName: file.name,
              status: 'duplicate',
              existing: r.data.existing
            };
          } else {
            copy[idx] = {
              fileName: file.name,
              status: 'ok',
              expense: r.data.expense,
              fieldsFilled: r.data.fields_filled || 0,
              extraction: r.data.extraction
            };
          }
          return copy;
        });
      } catch (err) {
        setItems(prev => {
          const copy = [...prev];
          copy[idx] = {
            fileName: file.name,
            status: 'error',
            error: err.response?.data?.message || err.message
          };
          return copy;
        });
      }
    }
    setRunning(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const reset = () => setItems([]);

  // Helper: patch a single expense and refresh the row so totals / display update.
  const patchExpense = async (rowIdx, patch) => {
    const it = items[rowIdx];
    if (!it?.expense?.id) return;
    const r = await api.patch(`/expenses/${it.expense.id}`, patch);
    setItems(prev => {
      const copy = [...prev];
      copy[rowIdx] = { ...copy[rowIdx], expense: r.data.expense || { ...copy[rowIdx].expense, ...patch } };
      return copy;
    });
  };

  // For the gross→net inline edit: parse "12,99" or "12.99" to cents
  const _parseEuroToCents = (s) => {
    if (typeof s !== 'string') return null;
    const t = s.replace(/\s|€|EUR/gi, '').replace(',', '.');
    const n = parseFloat(t);
    return Number.isFinite(n) ? Math.round(n * 100) : null;
  };

  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessResult, setReprocessResult] = useState(null);
  const reprocessEmpty = async () => {
    if (!confirm('Alle Belege mit 0,00 € erneut analysieren? Das kann je nach Anzahl mehrere Minuten dauern (OCR auf Scans).')) return;
    setReprocessing(true);
    setReprocessResult(null);
    try {
      const r = await api.post('/expenses/reprocess?only_empty=true');
      setReprocessResult(r.data);
    } catch (err) {
      setReprocessResult({ error: err.response?.data?.message || err.message });
    } finally {
      setReprocessing(false);
    }
  };

  const okCount = items.filter(i => i.status === 'ok').length;
  const errCount = items.filter(i => i.status === 'error').length;
  const dupCount = items.filter(i => i.status === 'duplicate').length;
  const totalNet = items.reduce((s, i) => s + (i.expense?.net_cents || 0), 0);
  const totalGross = items.reduce((s, i) => s + (i.expense?.gross_cents || 0), 0);

  return (
    <>
      <PageHeader title="Belege importieren"
        subtitle="Mehrere PDFs / Scans auf einmal hochladen — System legt pro Datei einen Ausgaben-Entwurf an und liest Lieferant, Datum, Betrag und MwSt. aus."
        actions={
          <>
            <Button variant="outline-secondary" as={Link} to="/expenses">
              <Icon icon="tabler:arrow-left" className="me-1" />Zu den Ausgaben
            </Button>
            <Button variant="outline-warning" onClick={reprocessEmpty} disabled={reprocessing}>
              <Icon icon="tabler:wand" className="me-1" />
              {reprocessing ? 'Analysiere…' : '0,00-Belege erneut analysieren'}
            </Button>
            {items.length > 0 && (
              <Button variant="outline-secondary" onClick={reset} disabled={running}>
                <Icon icon="tabler:refresh" className="me-1" />Liste leeren
              </Button>
            )}
          </>
        } />

      {reprocessResult && (
        <Alert variant={reprocessResult.error ? 'danger' : 'info'} className="mb-3" dismissible onClose={() => setReprocessResult(null)}>
          {reprocessResult.error
            ? <>Fehler: {reprocessResult.error}</>
            : <>Erneute Analyse abgeschlossen — <strong>{reprocessResult.updated}</strong> von <strong>{reprocessResult.processed}</strong> Belegen aktualisiert ({reprocessResult.skipped} übersprungen).</>}
        </Alert>
      )}

      <div
        className={`upload-dropzone mb-3 ${dragOver ? 'is-dragging' : ''} ${running ? 'is-busy' : ''}`}
        style={{ padding: '2.5rem' }}
        onClick={() => fileInput.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}>
        <Icon icon="tabler:cloud-upload" style={{ fontSize: 48 }} />
        <div className="fs-5">Belege hier ablegen</div>
        <div className="text-muted small">
          Mehrere Dateien gleichzeitig möglich · PDF, PNG, JPEG · max. 25 MB pro Datei
        </div>
        <Form.Control ref={fileInput} type="file" multiple hidden
          accept="application/pdf,image/png,image/jpeg,image/webp,image/gif,image/tiff"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} />
      </div>

      {items.length > 0 && (
        <>
          <div className="d-flex gap-3 mb-3 small">
            <span><Badge bg="" className="badge-soft-success me-1">{okCount}</Badge> erfolgreich</span>
            {dupCount > 0 && <span><Badge bg="" className="badge-soft-warning me-1">{dupCount}</Badge> Duplikat{dupCount === 1 ? '' : 'e'}</span>}
            {errCount > 0 && <span><Badge bg="" className="badge-soft-danger me-1">{errCount}</Badge> fehlgeschlagen</span>}
            <span className="ms-auto text-muted">
              Summe Netto: <strong>{fmtMoney(totalNet)}</strong> ·
              &nbsp;Brutto: <strong>{fmtMoney(totalGross)}</strong>
            </span>
          </div>

          <Card className="card-flat">
            <Card.Body className="p-0">
              <Table hover className="mb-0 align-middle small">
                <thead>
                  <tr>
                    <th>Datei</th>
                    <th>Status</th>
                    <th>Lieferant</th>
                    <th>Datum</th>
                    <th className="text-end">Netto</th>
                    <th>MwSt.</th>
                    <th className="text-end">Brutto</th>
                    <th>OCR</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => {
                    const isDup = it.status === 'duplicate';
                    const editable = it.status === 'ok' && it.expense;
                    return (
                    <tr key={i}>
                      <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.fileName}</td>
                      <td>{
                        it.status === 'pending'   ? <Badge bg="" className="badge-soft-secondary">Lädt…</Badge> :
                        it.status === 'ok'        ? <Badge bg="" className="badge-soft-success">OK</Badge> :
                        it.status === 'duplicate' ? <Badge bg="" className="badge-soft-warning">Duplikat</Badge> :
                                                    <Badge bg="" className="badge-soft-danger">Fehler</Badge>
                      }</td>
                      <td>{
                        isDup
                          ? <span className="text-warning small">bereits importiert{it.existing?.expense_number ? <> als <strong>{it.existing.expense_number}</strong></> : null}</span>
                          : editable
                            ? <InlineField value={it.expense.vendor} width={160}
                                onSave={(v) => patchExpense(i, { vendor: v })} />
                            : (it.error ? <span className="text-danger small">{it.error}</span> : '—')
                      }</td>
                      <td>{
                        isDup
                          ? (it.existing?.expense_date ? fmtDate(it.existing.expense_date) : '—')
                          : editable
                            ? <InlineField type="date" value={it.expense.expense_date} width={140}
                                onSave={(v) => patchExpense(i, { expense_date: v || null })} />
                            : (it.expense?.expense_date ? fmtDate(it.expense.expense_date) : '—')
                      }</td>
                      <td className="text-end">{it.expense ? fmtMoney(it.expense.net_cents, it.expense.currency) : '—'}</td>
                      <td>{
                        editable
                          ? <InlineField value={String(it.expense.vat_rate)} width={56} suffix="%" align="right"
                              onSave={(v) => {
                                const vat = parseFloat(String(v).replace(',', '.'));
                                if (Number.isFinite(vat) && vat >= 0) return patchExpense(i, { vat_rate: vat });
                              }} />
                          : (it.expense ? `${it.expense.vat_rate}%` : '—')
                      }</td>
                      <td className="text-end">{
                        isDup
                          ? (it.existing?.gross_cents ? fmtMoney(it.existing.gross_cents, it.existing.currency) : '—')
                          : editable
                            ? <InlineField
                                value={(it.expense.gross_cents/100).toFixed(2).replace('.', ',')}
                                width={90} suffix=" €" align="right"
                                onSave={(v) => {
                                  const cents = _parseEuroToCents(v);
                                  if (cents == null) return;
                                  // recompute net from gross + vat_rate
                                  const vat = it.expense.vat_rate || 0;
                                  const netCents = Math.round(cents / (1 + vat/100));
                                  return patchExpense(i, { net_cents: netCents });
                                }} />
                            : (it.expense ? fmtMoney(it.expense.gross_cents, it.expense.currency) : '—')
                      }</td>
                      <td>
                        {isDup
                          ? <span className="text-muted">übersprungen</span>
                          : it.fieldsFilled > 0
                            ? <Badge bg="" className="badge-soft-primary">{it.fieldsFilled}</Badge>
                            : it.status === 'ok' ? <span className="text-muted">—</span> : '—'}
                      </td>
                      <td className="text-end pe-3">
                        {it.expense && (
                          <Button size="sm" variant="outline-secondary"
                            onClick={() => navigate(`/expenses?edit=${it.expense.id}`)}>
                            Öffnen
                          </Button>
                        )}
                        {isDup && it.existing?.expense_id && (
                          <Button size="sm" variant="outline-warning"
                            onClick={() => navigate(`/expenses?edit=${it.existing.expense_id}`)}>
                            Original
                          </Button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <Alert variant="info" className="mt-3 small">
            <Icon icon="tabler:info-circle" className="me-1" />
            Alle erfolgreich importierten Belege liegen jetzt als <strong>Entwurf</strong> in den
            Ausgaben. Du kannst dort jeden Eintrag prüfen, korrigieren und auf „Bezahlt" setzen.
          </Alert>
        </>
      )}
    </>
  );
}
